import React, { useEffect, useState } from 'react';
import { apiGet } from '../../lib/api';
import { Upload, Download, FileText, Trash2, Filter } from 'lucide-react';
import { toast } from 'sonner';
import DriveAttachments from '../../components/DriveAttachments';

const CATEGORIES = ['RFQ', 'Proposal', 'Presentation', 'NDA', 'Contract', 'Other'];

export default function DocumentRepository() {
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [filters, setFilters] = useState({ entity_type: '', category: '' });
  const [uploadForm, setUploadForm] = useState({
    entity_type: 'lead', entity_id: '', category: 'RFQ'
  });

  const loadDocuments = async () => {
    try {
      const params = new URLSearchParams(filters);
      const res = await apiGet(`/crm/documents?${params}`);
      setDocuments(res.documents || []);
    } catch (err) {
      console.error('Failed to load documents:', err);
    }
  };

  useEffect(() => { loadDocuments(); }, [filters]);

  const handleUpload = async (e) => {
    e.preventDefault();
    const fileInput = document.getElementById('file-upload');
    const file = fileInput?.files[0];
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error('File too large (max 50MB)');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams({
        entity_type: uploadForm.entity_type,
        entity_id: uploadForm.entity_id,
        category: uploadForm.category
      });

      const response = await fetch(`${backendUrl}/api/crm/documents/upload?${params}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      toast.success('Document uploaded successfully');
      setUploadForm({ entity_type: 'lead', entity_id: '', category: 'RFQ' });
      fileInput.value = '';
      loadDocuments();
    } catch (err) {
      toast.error(err.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${backendUrl}/api/crm/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (err) {
      toast.error('Failed to download document');
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div>
      <div className="mb-6">
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">Business Development</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Document Repository</h1>
        <p className="text-sm text-slate-600 mt-1">Store and manage RFQs, proposals, and client documents</p>
      </div>

      {/* Upload Form */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
        <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
          <Upload size={20} /> Upload Document
        </h2>
        <form onSubmit={handleUpload} className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="label">Entity Type *</label>
              <select value={uploadForm.entity_type} onChange={(e) => setUploadForm({...uploadForm, entity_type: e.target.value})} className="input" required>
                <option value="lead">Lead</option>
                <option value="opportunity">Opportunity</option>
                <option value="project">Project</option>
              </select>
            </div>
            <div>
              <label className="label">Entity ID *</label>
              <input type="text" value={uploadForm.entity_id} onChange={(e) => setUploadForm({...uploadForm, entity_id: e.target.value})} className="input" placeholder="Lead/Opportunity/Project ID" required />
            </div>
            <div>
              <label className="label">Category *</label>
              <select value={uploadForm.category} onChange={(e) => setUploadForm({...uploadForm, category: e.target.value})} className="input" required>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label">File (Max 50MB) *</label>
            <input id="file-upload" type="file" className="input" required />
          </div>
          <button type="submit" disabled={uploading} className="btn-primary flex items-center gap-2">
            {uploading ? 'Uploading...' : <><Upload size={16} /> Upload Document</>}
          </button>
        </form>

        {uploadForm.entity_id && (
          <div className="mt-5 pt-5 border-t border-slate-200">
            <DriveAttachments
              contextType={uploadForm.entity_type}
              contextId={uploadForm.entity_id}
              category={uploadForm.category}
              title={`Drive Attachments for ${uploadForm.entity_type} ${uploadForm.entity_id}`}
            />
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4 flex gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-slate-500" />
          <span className="text-xs font-display uppercase text-slate-600">Filters:</span>
        </div>
        <select value={filters.entity_type} onChange={(e) => setFilters({...filters, entity_type: e.target.value})} className="input text-xs">
          <option value="">All Entities</option>
          <option value="lead">Leads</option>
          <option value="opportunity">Opportunities</option>
          <option value="project">Projects</option>
        </select>
        <select value={filters.category} onChange={(e) => setFilters({...filters, category: e.target.value})} className="input text-xs">
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Documents List */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full dense-table">
          <thead>
            <tr>
              <th>File Name</th>
              <th>Category</th>
              <th>Entity</th>
              <th>Size</th>
              <th>Uploaded By</th>
              <th>Upload Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {documents.map(doc => (
              <tr key={doc.id}>
                <td>
                  <div className="flex items-center gap-2">
                    <FileText size={16} className="text-[#1F7A6E]" />
                    <span className="font-display font-semibold text-sm">{doc.original_filename}</span>
                  </div>
                </td>
                <td><span className="tag bg-teal-100 text-teal-700">{doc.category}</span></td>
                <td className="text-xs">
                  <div>{doc.entity_type}</div>
                  <div className="text-slate-500">{doc.entity_id}</div>
                </td>
                <td className="text-xs">{formatBytes(doc.size)}</td>
                <td className="text-xs">{doc.uploaded_by}</td>
                <td className="text-xs">{new Date(doc.created_at).toLocaleDateString()}</td>
                <td>
                  <button onClick={() => handleDownload(doc)} className="text-[#1F7A6E] hover:text-[#14504E]">
                    <Download size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {documents.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-500">No documents uploaded yet.</div>
        )}
      </div>
    </div>
  );
}
